// Command name → REST route.
//
// The alternative was an RPC endpoint on the API that mirrors the WebView bridge one-for-one.
// Rejected: it would give the catalog a second, parallel surface that has to be kept in sync with
// the REST one, and it would make the public API unusable by anything but this client. An explicit
// map is a few dozen lines and keeps the server plainly RESTful.
//
// A command absent from this map is Revit-only by definition. That is not an oversight to be
// patched later — it is how the browser learns it cannot load a family into a project.

import type { HttpRoute } from "./types.http";

export const HTTP_ROUTES: Readonly<Record<string, HttpRoute>> = {
  // ── Catalog: available in both environments ───────────────────────────────────────────────
  // Inside Revit these still go through C# (which holds the access token) and land on the same
  // endpoints; in the browser the SPA calls them directly with its session cookie.
  GetServerInfo: { method: "GET", path: "/api/info" },

  // Who the browser is signed in as. Inside Revit the host answers this instead, from the token it
  // holds; the shape is identical so the UI reads one thing in both environments.
  GetAuthState: { method: "GET", path: "/api/auth/state" },
  SignOut: { method: "POST", path: "/api/auth/sign-out" },
  BrowseFamilies: { method: "GET", path: "/api/families" },
  BrowseFamilyTree: { method: "GET", path: "/api/families/tree" },
  GetParameterOptions: { method: "GET", path: "/api/families/parameters" },
  GetFamilyDetail: {
    method: "GET",
    // The slug is a path segment; encode it since slugs are user-derived.
    path: (p: { slug: string }) => `/api/families/${encodeURIComponent(p.slug)}`,
  },
  DeleteFamily: {
    method: "DELETE",
    path: (p: { slug: string }) => `/api/families/${encodeURIComponent(p.slug)}`,
  },

  // ── Personal marks ───────────────────────────────────────────────────────────────────────
  // One command, two verbs: marking is a PUT and clearing is a DELETE, chosen from the payload the
  // same way a path segment is. Idempotent in both directions, which a POST toggle would not be.
  SetFavorite: {
    method: (p: { favorite: boolean }) => (p.favorite ? "PUT" : "DELETE"),
    path: (p: { slug: string }) => `/api/families/${encodeURIComponent(p.slug)}/favorite`,
  },
  GetPinnedLocations: { method: "GET", path: "/api/families/pinned" },
  PinLocation: { method: "PUT", path: "/api/families/pinned" },
  UnpinLocation: { method: "POST", path: "/api/families/pinned/remove" },
  RecordFamilyUsage: {
    method: "POST",
    path: (p: { slug: string }) => `/api/families/${encodeURIComponent(p.slug)}/usage`,
  },

  // ── Moderation: both environments, gated on the Moderator role by the API ────────────────
  GetModerationQueue: { method: "GET", path: "/api/moderation/queue" },
  GetModerationDownload: {
    method: "GET",
    path: (p: { slug: string }) => `/api/moderation/${encodeURIComponent(p.slug)}/download`,
  },
  EditModerationFamily: {
    method: "PATCH",
    path: (p: { slug: string }) => `/api/moderation/${encodeURIComponent(p.slug)}/edit`,
  },
  ApproveFamily: {
    method: "POST",
    path: (p: { slug: string }) => `/api/moderation/${encodeURIComponent(p.slug)}/approve`,
  },
  RejectFamily: {
    method: "POST",
    path: (p: { slug: string }) => `/api/moderation/${encodeURIComponent(p.slug)}/reject`,
  },

  // ── Organizations: both environments, always authenticated ───────────────────────────────
  // Reachable from the browser now that the API accepts its session cookie; inside Revit the same
  // command names resolve to plugin commands, which attach the host-held token instead.
  GetMyOrganization: { method: "GET", path: "/api/orgs/mine" },
  ListOrganizations: { method: "GET", path: "/api/orgs" },
  CreateOrganization: { method: "POST", path: "/api/orgs" },
  GetOrganization: {
    method: "GET",
    path: (p: { slug: string }) => `/api/orgs/${encodeURIComponent(p.slug)}`,
  },
  AddOrganizationMember: {
    method: "POST",
    path: (p: { slug: string }) => `/api/orgs/${encodeURIComponent(p.slug)}/members`,
  },
  SetOrganizationMemberRole: {
    method: "PATCH",
    path: (p: { slug: string; userId: string }) =>
      `/api/orgs/${encodeURIComponent(p.slug)}/members/${encodeURIComponent(p.userId)}`,
  },
  RemoveOrganizationMember: {
    method: "DELETE",
    path: (p: { slug: string; userId: string }) =>
      `/api/orgs/${encodeURIComponent(p.slug)}/members/${encodeURIComponent(p.userId)}`,
  },

  // ── Revit-only commands are deliberately NOT listed ───────────────────────────────────────
  // GetHostInfo, SignIn, DownloadAndLoad, DownloadAndPlace, ExtractFamilyPackage, PickFiles, ...
  // These need a Revit session or the local file system, so the browser gets a
  // CommandUnavailableError it renders as "open this in Revit".
};

export function findRoute(command: string): HttpRoute | undefined {
  return HTTP_ROUTES[command];
}
